import { useState, useEffect } from 'react';
import { Box, Tooltip, Chip, Snackbar, Alert, Slide } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';

function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

function InternetStatusIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [lastVerifiedAt, setLastVerifiedAt] = useState(null);

  // Perform a lightweight external network probe to truly verify connectivity.
  // Uses a CORS-safe pattern: mode: 'no-cors'. If the network is down, fetch rejects.
  const probeUrls = [
    'https://www.gstatic.com/generate_204',
    'https://www.google.com/favicon.ico',
  ];

  const checkConnectivity = async (timeoutMs = 5000) => {
    const tryFetch = async (url) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        await fetch(url, {
          mode: 'no-cors',
          cache: 'no-store',
          signal: controller.signal,
        });
        clearTimeout(timer);
        return true; // Request reached the network; success implies connectivity
      } catch (e) {
        clearTimeout(timer);
        return false;
      }
    };

    for (const url of probeUrls) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await tryFetch(url);
      if (ok) return true;
    }
    return false;
  };

  useEffect(() => {
    const handleOnline = async () => {
      const verified = await checkConnectivity();
      setIsOnline(verified);
      setLastVerifiedAt(new Date());
      if (verified) {
        setNotificationMessage("You're back online");
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      } else {
        // Edge: Browser reports online, but probe failed
        setNotificationMessage('Network issue detected');
        setShowNotification(true);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setLastVerifiedAt(new Date());
      setNotificationMessage("You don't have internet connection");
      setShowNotification(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial verification regardless of navigator.onLine
    (async () => {
      const verified = await checkConnectivity();
      setIsOnline(verified);
      setLastVerifiedAt(new Date());
      if (!verified) {
        setNotificationMessage("You don't have internet connection");
        setShowNotification(true);
      }
    })();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleCloseNotification = (event, reason) => {
    // Only allow manual close for offline status
    if (reason === 'clickaway' && !isOnline) {
      return;
    }
    setShowNotification(false);
  };

  // Periodic re-verification to keep status accurate
  useEffect(() => {
    const interval = setInterval(async () => {
      const verified = await checkConnectivity();
      if (verified !== isOnline) {
        setIsOnline(verified);
        setLastVerifiedAt(new Date());
        setNotificationMessage(verified ? "You're back online" : "You don't have internet connection");
        setShowNotification(true);
        if (verified) setTimeout(() => setShowNotification(false), 3000);
      } else {
        setLastVerifiedAt(new Date());
      }
    }, 15000); // every 15s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  return (
    <>
      {/* Always visible status indicator */}
      <Box
        sx={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: 9999,
        }}
      >
        <Tooltip 
          title={isOnline ? 'Online (verified)' : 'Offline'} 
          placement="left"
          arrow
        >
          <Chip
            icon={isOnline ? <WifiIcon /> : <WifiOffIcon />}
            label={isOnline ? 'Online' : 'Offline'}
            color={isOnline ? 'success' : 'error'}
            size="small"
            sx={{
              fontWeight: '600',
              fontSize: '12px',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
              cursor: 'default',
              '& .MuiChip-icon': {
                fontSize: '16px',
              },
            }}
          />
        </Tooltip>
      </Box>

      {/* Notification - Shows when status changes */}
      <Snackbar
        open={showNotification}
        autoHideDuration={isOnline ? 3000 : null}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={SlideTransition}
        sx={{ 
          top: '60px !important',
          right: '16px !important',
          zIndex: 9998
        }}
      >
        <Alert
          severity={isOnline ? 'success' : 'error'}
          icon={isOnline ? <WifiIcon /> : <WifiOffIcon />}
          onClose={handleCloseNotification}
          sx={{
            width: '100%',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default InternetStatusIndicator;
